import { CheckCircleOutlined, DiffOutlined, SelectOutlined, UserOutlined } from '@ant-design/icons';
import { Button, Card, message, Modal, Row, Select, Space, Spin, Typography } from 'antd';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Trans, useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { createSearchParams, Outlet } from 'react-router-dom';
import { useDebouncedCallback } from 'use-debounce';

import useAPIMutation from '../api/useAPIMutation';
import useAPIQuery from '../api/useAPIQuery';
import useInfiniteAPIQuery from '../api/useInfiniteAPIQuery';
import Config from '../app/config';
import ErrorResult from '../components/ErrorResult';
import Form from '../components/Form';
import Page from '../components/Page';
import PageContent from '../components/PageContent';
import PageTitle from '../components/PageTitle';
import RichEditor from '../components/RichEditor';
import { fixUnorderedListInQuill } from '../components/RichEditor/RichEditor';
import usePrefetchPages from '../hooks/usePrefetchPages';
import useTypedNavigate from '../hooks/useTypedNavigate';
import useTypedParams from '../hooks/useTypedParams';
import { JobPostingId } from '../types';
import styles from './CandidateInterview.module.css';

interface CandidateInterviewFormValues {
  annotations: string;
  interviewScriptTemplateId: number;
  stageId: number;
}

const CandidateInterview = () => {
  const { t } = useTranslation();

  const [form] = Form.useForm();

  const { tenant, interview_id, id } = useTypedParams({
    tenant: String,
    id: JobPostingId,
    interview_id: Number,
  });

  const navigate = useTypedNavigate();
  const queryClient = useQueryClient();

  const interview = useAPIQuery(`/interviews/${interview_id}/`);

  const recruitmentProcess = useAPIQuery(
    `/recruitment-processes/${interview.data?.recruitment_process}/`,
    { enabled: Boolean(interview.data?.recruitment_process) }
  );

  const stage = useAPIQuery(
    [`/stages/${interview.data?.stage}/`, { params: { expand: 'interview_script_content' } }],
    { enabled: Boolean(interview.data?.recruitment_process) }
  );

  const stageEmailCommunicationTemplatePracticalTechnicalCase = useAPIQuery(
    `/email-communication-templates/${stage.data?.email_communication_template}/`,
    {
      enabled:
        stage.data?.sub_type === 'PRACTICAL_TECHNICAL_CASE' &&
        !!stage.data?.email_communication_template,
    }
  );

  const interviewScriptTemplatePages = useInfiniteAPIQuery(
    [
      '/interview-script-templates/',
      {
        params: {
          type: stage.data?.sub_type,
        },
      },
    ],
    { enabled: Boolean(stage.data?.sub_type) }
  );

  const interviewScriptTemplates = usePrefetchPages(
    interviewScriptTemplatePages,
    (data) => data.results
  );

  const interviewScriptTemplatesOptions = interviewScriptTemplates.map(
    (interviewScriptTemplate) => ({
      key: `${interviewScriptTemplate.id}-${interviewScriptTemplate.name}`,
      value: interviewScriptTemplate.id,
      label: interviewScriptTemplate.name,
    })
  );

  const interviewScriptTemplatesOptionsWithPersonalized = stage.data?.lock_interview_script
    ? []
    : [
        ...(stage.data?.interview_script_template === null &&
        ((stage.data?.interview_script_content || '').length > 0 ||
          (stageEmailCommunicationTemplatePracticalTechnicalCase.data?.message || '').length > 0)
          ? [
              {
                key: `personalizado`,
                value: 0,
                label: t('Personalizado'),
              },
            ]
          : []),
        ...interviewScriptTemplatesOptions,
      ];

  const jobPosting = useAPIQuery(`/job-postings/${id}/`);

  const finishInterview = useAPIMutation(
    async (
      { annotations, stageId, interviewScriptTemplateId }: CandidateInterviewFormValues,
      api
    ) => {
      const stageData = await api.get(`/stages/${stageId}/`);
      if (!stageData.data.lock_interview_script) {
        if (interviewScriptTemplateId !== 0)
          await api.patch(`/stages/${stage.data?.id}/`, {
            interview_script_template: interviewScriptTemplateId,
          });
      }

      return await api.patch(`/interviews/${interview_id}/`, { status: 'FINISHED', annotations });
    },
    {
      onSettled: async () => {
        await queryClient.invalidateQueries('/interviews/');
        await queryClient.invalidateQueries(`/interviews/${interview_id}/`);
      },
      onSuccess: async ({ data }) => {
        message.success(t('Entrevista finalizada com sucesso'));
        navigate({
          pathname: '/:tenant/opportunities/:id/interview/:interview_id/opinion/',
          params: {
            tenant,
            id,
            interview_id,
          },
          search: createSearchParams(
            `stages=${data.stage}&status=ACTIVE&sort=match&direction=descend`
          ),
        });
      },
    }
  );

  const [isSavedVisible, setIsSavedVisible] = useState(false);
  const interviewScriptTemplateIdValue = Form.useWatch<
    CandidateInterviewFormValues['interviewScriptTemplateId'] | undefined
  >('interviewScriptTemplateId', form);

  const retrieveInterviewScriptContent = (
    interviewScriptTemplateId: CandidateInterviewFormValues['interviewScriptTemplateId'] | undefined
  ) => {
    if (interviewScriptTemplateId === 0) return stage.data?.interview_script_content || '';
    return (
      interviewScriptTemplates.find(
        (interviewScriptTemplate) => interviewScriptTemplate.id === interviewScriptTemplateId
      )?.content || ''
    );
  };

  const interviewScriptTemplateEmailCommunicationTemplateId = interviewScriptTemplates.find(
    (ist) => ist.id === interviewScriptTemplateIdValue
  )?.email_communication_template;

  const interviewScriptTemplateEmailCommunicationTemplate = useAPIQuery(
    `/email-communication-templates/${interviewScriptTemplateEmailCommunicationTemplateId}/`,
    {
      enabled:
        stage.data?.sub_type === 'PRACTICAL_TECHNICAL_CASE' &&
        Boolean(interviewScriptTemplateEmailCommunicationTemplateId) &&
        !!interviewScriptTemplateIdValue &&
        interviewScriptTemplateIdValue > 0,
    }
  );

  const retrieveInterviewScriptCandidateContent = (
    interviewScriptTemplateId: CandidateInterviewFormValues['interviewScriptTemplateId'] | undefined
  ) => {
    if (interviewScriptTemplateId === 0 || stage.data?.lock_interview_script)
      return stageEmailCommunicationTemplatePracticalTechnicalCase.data?.message || '';
    return interviewScriptTemplateEmailCommunicationTemplate.data?.message;
  };

  const updateAnnotationsMutation = useAPIMutation(
    async ({ annotations }: { annotations: string }, api) => {
      await api.patch(`/interviews/${interview_id}/`, { annotations });
    },
    {
      onSuccess: () => {
        setIsSavedVisible(true);
      },
    }
  );

  const debounceUpdateAnnotation = useDebouncedCallback(
    ({ annotations }: { annotations: string }) => {
      updateAnnotationsMutation.mutate({
        annotations,
      });
    },
    Config.REACT_APP_AUTOSAVE_DELAY
  );

  return (
    <Page>
      <ErrorResult
        error={recruitmentProcess.error || stage.error || interview.error || jobPosting.error}
      >
        <Spin
          spinning={
            recruitmentProcess.isLoading ||
            stage.isLoading ||
            interview.isLoading ||
            jobPosting.isLoading
          }
        >
          <Helmet>
            <title>
              {t('Entrevista')} | {recruitmentProcess.data?.candidate?.name || '...'} |{' '}
              {t('Recrutamento e Seleção')}
            </title>
          </Helmet>
          <Outlet />
          <PageTitle
            title={`Entrevista | ${recruitmentProcess.data?.candidate.name || '...'}`}
            level={2}
            extra={[
              ...(stage.data?.sub_type === 'PRACTICAL_TECHNICAL_CASE'
                ? [
                    <Button
                      key="email-communication-template-description"
                      icon={<DiffOutlined />}
                      onClick={() =>
                        Modal.info({
                          title: t('Descrição para o candidato'),
                          content: (
                            <div
                              dangerouslySetInnerHTML={{
                                __html:
                                  retrieveInterviewScriptCandidateContent(
                                    interviewScriptTemplateIdValue
                                  ) || '',
                              }}
                              style={{ maxHeight: 600, overflowY: 'scroll' }}
                            />
                          ),
                          width: 800,
                          closable: true,
                          maskClosable: true,
                        })
                      }
                      disabled={
                        !(
                          retrieveInterviewScriptCandidateContent(interviewScriptTemplateIdValue) ||
                          ''
                        )
                      }
                      loading={interviewScriptTemplateEmailCommunicationTemplate.isLoading}
                    >
                      {' '}
                      <Trans>Descrição do case</Trans>
                    </Button>,
                  ]
                : []),
              <Button
                key="job-posting-description"
                icon={<SelectOutlined />}
                onClick={() =>
                  Modal.info({
                    title: t('Descrição da vaga'),
                    content: (
                      <div
                        dangerouslySetInnerHTML={{ __html: jobPosting.data?.description || '' }}
                        style={{ maxHeight: 600, overflowY: 'scroll' }}
                      />
                    ),
                    width: 800,
                    closable: true,
                    maskClosable: true,
                  })
                }
              >
                {' '}
                <Trans>Descrição da vaga</Trans>
              </Button>,
              <Button
                key="candidate-data"
                icon={<UserOutlined />}
                onClick={() =>
                  navigate(
                    {
                      pathname:
                        '/:tenant/opportunities/:id/interview/:interview_id/candidate/:applicant_id/details/',
                      params: {
                        tenant,
                        id,
                        interview_id,
                        applicant_id: recruitmentProcess.data?.candidate.id,
                      },
                    },
                    { replace: true }
                  )
                }
              >
                {' '}
                <Trans>Dados do candidato</Trans>
              </Button>,
              <Button
                key="finish"
                type="primary"
                loading={finishInterview.isLoading}
                onClick={async () => {
                  if (stage.data?.id) {
                    const interviewScriptTemplateId = form.getFieldValue(
                      'interviewScriptTemplateId'
                    );

                    try {
                      await form.validateFields(['interviewScriptTemplateId']);
                      finishInterview.mutate({
                        annotations: fixUnorderedListInQuill(form.getFieldValue('annotations')),
                        interviewScriptTemplateId,
                        stageId: stage.data.id,
                      });
                    } catch (err) {
                      return undefined;
                    }
                  }
                }}
              >
                <Trans>Próximo</Trans>
              </Button>,
            ]}
          />
          <PageContent>
            <Card>
              <Form<CandidateInterviewFormValues>
                form={form}
                layout="vertical"
                initialValues={{
                  annotations: interview.data?.annotations || '',
                  interviewScriptTemplateId: stage.data?.interview_script_template
                    ? stage.data?.interview_script_template
                    : stage.data?.interview_script_content ||
                      stage.data?.email_communication_template
                    ? 0
                    : undefined,
                }}
                onValuesChange={(changedValues, values) => {
                  if (changedValues.annotations) {
                    debounceUpdateAnnotation({
                      annotations:
                        values.annotations === '<p><br></p>'
                          ? ''
                          : fixUnorderedListInQuill(values.annotations),
                    });

                    if (isSavedVisible) {
                      setIsSavedVisible(false);
                    }
                  }
                }}
                onFinish={async ({ annotations }) => {
                  debounceUpdateAnnotation.cancel();
                  if (isSavedVisible) {
                    setIsSavedVisible(false);
                  }
                  await updateAnnotationsMutation.mutateAsync({
                    annotations:
                      annotations === '<p><br></p>' ? '' : fixUnorderedListInQuill(annotations),
                  });
                  message.success(t('Anotações atualizadas com sucesso!'));
                }}
              >
                <Form.Row>
                  <Form.Col span={12}>
                    <Form.Legend>
                      <Trans>Anotações da entrevista</Trans>
                    </Form.Legend>
                    <Space direction="vertical" size="middle">
                      <Typography.Text type="secondary">
                        <Trans>
                          Registre perguntas, respostas, comportamentos e informações para a
                          avaliação
                        </Trans>
                      </Typography.Text>
                      <Form.Item noStyle name="annotations">
                        <RichEditor
                          image={false}
                          video={false}
                          readOnly={finishInterview.isLoading}
                        />
                      </Form.Item>
                      <Row justify="space-between" align="middle">
                        {isSavedVisible ? (
                          <Typography.Text>
                            <CheckCircleOutlined className={styles.success} />
                            <Trans>Salvo</Trans>
                          </Typography.Text>
                        ) : (
                          <div style={{ height: 22 }} />
                        )}
                        <Button
                          loading={updateAnnotationsMutation.isLoading}
                          onClick={() => form.submit()}
                        >
                          <Trans>Salvar</Trans>
                        </Button>
                      </Row>
                    </Space>
                  </Form.Col>
                  <Form.Col span={12}>
                    <Space direction="vertical" size="middle">
                      {stage.data?.lock_interview_script ? (
                        <>
                          <Typography.Text>
                            <Trans>Roteiro da etapa</Trans>
                          </Typography.Text>
                          <Select
                            disabled
                            style={{ width: '100%' }}
                            value={stage.data?.interview_script_name}
                            options={[
                              {
                                key: stage.data?.interview_script_name,
                                value: stage.data?.interview_script_name,
                                label: stage.data?.interview_script_name,
                              },
                            ]}
                          />
                          <div
                            dangerouslySetInnerHTML={{
                              __html: stage.data?.interview_script_content || '',
                            }}
                          />
                        </>
                      ) : (
                        <>
                          <Form.Item
                            name="interviewScriptTemplateId"
                            label={t('Roteiro da etapa')}
                            required
                            rules={[{ required: true, message: t('Escolha o roteiro') }]}
                          >
                            <Select
                              placeholder={t('Escolha o roteiro')}
                              style={{ width: '100%' }}
                              options={interviewScriptTemplatesOptionsWithPersonalized}
                            />
                          </Form.Item>
                          <div
                            dangerouslySetInnerHTML={{
                              __html: retrieveInterviewScriptContent(
                                interviewScriptTemplateIdValue
                              ),
                            }}
                          />
                        </>
                      )}
                    </Space>
                  </Form.Col>
                </Form.Row>
              </Form>
            </Card>
          </PageContent>
        </Spin>
      </ErrorResult>
    </Page>
  );
};

export default CandidateInterview;
